/** Help §5.4 — How a project works (`project/StageStepper.tsx`, `project/projectDetailModel.ts`). */
import { helpSectionMeta } from '../help-sections.js';
import { Btn, Bullets, Callout, HelpSectionCard, Li, P, Screen, Shot } from '../HelpPrimitives.js';

/**
 * Name + what the stage is actually FOR. The stepper renders the names, the list
 * below renders both, so a stage can never appear in one and not the other.
 */
const STAGES = [
  {
    name: 'Pre-Kickoff',
    what: (
      <>
        gather the basics. Add what you know about the role, let <Btn>AI Analysis</Btn> read it and
        fill in the <Screen>Project Overview</Screen>, then decide how to proceed.
      </>
    ),
  },
  {
    name: 'Kick-off',
    what: <>work through a checklist of questions about the client and the ideal candidate.</>,
  },
  {
    name: 'Generation',
    what: (
      <>
        the AI writes the Kickoff Sheet, Job Description, InMails and the rest; you review and
        approve each one.
      </>
    ),
  },
  {
    name: 'Launched',
    what: <>every document is approved and the final package has been sent.</>,
  },
] as const;

export function Stages(): React.JSX.Element {
  return (
    <HelpSectionCard meta={helpSectionMeta('stages')}>
      <P>Every project moves through four stages, shown as a stepper at the top of its card:</P>
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-3">
        {STAGES.map((stage, index) => (
          <li key={stage.name} className="flex items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface-muted py-1.5 pl-1.5 pr-3.5">
              <span
                aria-hidden
                className="brand-font flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-accent"
              >
                {index + 1}
              </span>
              <span className="text-sm font-medium text-text-strong">{stage.name}</span>
            </span>
            {index < STAGES.length - 1 ? (
              <i className="fa-solid fa-arrow-right text-xs text-text-muted" aria-hidden />
            ) : null}
          </li>
        ))}
      </ol>
      <Bullets>
        {STAGES.map((stage) => (
          <Li key={stage.name}>
            <Screen>{stage.name}</Screen> — {stage.what}
          </Li>
        ))}
      </Bullets>
      <Shot
        slug="stage-stepper"
        caption="The stepper at the top of a project — the current stage is highlighted in gold."
      />
      <Callout title="Work through the stages in order">
        <P>
          Each stage feeds the next one, so skipping ahead produces worse results rather than saving
          time: <Btn>AI Analysis</Btn> in Pre-Kickoff is what <Btn>AI Pre-fill Questionnaire</Btn>{' '}
          draws on in Kick-off, and everything you answer in Kick-off is what the AI reads when it
          writes the documents in Generation. A project you rush through Pre-Kickoff or Kick-off
          shows up later as a Kickoff Sheet or Job Description with gaps in it.
        </P>
      </Callout>
      <Callout tone="change" title="New">
        <P>
          You can click a past or future stage in the stepper to <em>look</em> at it without moving
          the project. The app then shows a banner —{' '}
          <em>“Viewing stage &lt;name&gt;. Current project stage: &lt;current&gt;”</em> — with a{' '}
          <Btn>Back to current</Btn> button to return.
        </P>
      </Callout>
    </HelpSectionCard>
  );
}
